import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import type { ThemeStyle } from '../tamagui.config'
import { THEME_STYLES } from '../tamagui.config'

const LS_KEY = 'torch-theme-style'

interface ThemeContextValue {
  style: ThemeStyle
  setStyle: (style: ThemeStyle) => void
  tamaguiTheme: string
}

const ThemeContext = createContext<ThemeContextValue>({
  style: 'campfire',
  setStyle: () => {},
  tamaguiTheme: 'dark_campfire',
})

function resolveThemeName(style: ThemeStyle): string {
  return `dark_${style}`
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [style, setStyleState] = useState<ThemeStyle>(() => {
    try {
      const stored = localStorage.getItem(LS_KEY)
      if (stored && stored in THEME_STYLES) return stored as ThemeStyle
    } catch {}
    return 'campfire'
  })

  const setStyle = useCallback((s: ThemeStyle) => {
    setStyleState(s)
    try {
      localStorage.setItem(LS_KEY, s)
    } catch {}
  }, [])

  const tamaguiTheme = resolveThemeName(style)

  useEffect(() => {
    const bgMap: Record<string, string> = {
      torch: '#0a0908',
      hades: '#0c0806',
      gow: '#0a0c10',
      odyssey: '#080c12',
      rome: '#0c0a08',
      spacex: '#000000',
      coinbase: '#0a0b0d',
      diablo: '#0a0a0c',
      campfire: '#0c0b0a',
    }
    const bg = bgMap[style] || '#0a0908'
    document.documentElement.style.backgroundColor = bg
    document.body.style.backgroundColor = bg
  }, [style])

  return (
    <ThemeContext.Provider value={{ style, setStyle, tamaguiTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  return useContext(ThemeContext)
}
